import {
  applyBannerSubtractKnockoutMask,
  getLineSegments,
  removeBannerKnockoutMaskDef,
  whenHasLayout,
} from "../utils/text-mask-geometry.js";

const RESIZE_DEBOUNCE_MS = 300;

const HERO_WHITE_CUTOUTS = [
  {
    section: ".banner",
    cutout: ".banner__container",
    highlight: ".videoWordSlot",
  }
];

function updateWhiteCutoutMask(cutoutEl, highlightEl) {
  const w = cutoutEl.clientWidth;
  const h = cutoutEl.clientHeight;
  if (w <= 0 || h <= 0) return;

  const style = getComputedStyle(highlightEl);
  const fontSizePx = parseFloat(style.fontSize) || 16;
  // Mask is applied to `cutoutEl`; SVG coords must use the same origin/size as that box.
  const mediaRect = cutoutEl.getBoundingClientRect();
  const segments = getLineSegments(highlightEl, mediaRect, fontSizePx, style.direction);
  if (!segments.length) return;

  applyBannerSubtractKnockoutMask(cutoutEl, segments, w, h, style);
}

/**
 * White layers with SVG subtract masks so the single hero background video shows through
 * `.banner__title-highlight` and `.content-col-layout__title-highlight` glyphs only.
 */
export function initBannerHeroVideoCutout() {
  const bindings = [];

  for (const cfg of HERO_WHITE_CUTOUTS) {
    const sectionEl = document.querySelector(cfg.section);
    if (!sectionEl) continue;
    const cutout = sectionEl.querySelector(cfg.cutout);
    const highlight = sectionEl.querySelector(cfg.highlight);
    if (!cutout || !highlight) continue;
    bindings.push({ sectionEl, cutout, highlight });
  }

  if (!bindings.length) {
    return () => {};
  }

  let maskRafId = null;
  const schedule = () => {
    if (maskRafId != null) return;
    maskRafId = requestAnimationFrame(() => {
      maskRafId = null;
      for (const { cutout, highlight } of bindings) {
        updateWhiteCutoutMask(cutout, highlight);
      }
    });
  };

  const run = () => {
    whenHasLayout(bindings[0].cutout, () => {
      schedule();
      requestAnimationFrame(schedule);
    });
  };

  if (document.fonts && typeof document.fonts.ready?.then === "function") {
    document.fonts.ready.then(run).catch(run);
  } else {
    run();
  }

  const ro = new ResizeObserver(() => schedule());
  for (const { sectionEl, cutout, highlight } of bindings) {
    ro.observe(sectionEl);
    ro.observe(cutout);
    ro.observe(highlight);
  }

  let resizeTimeout;
  const onWindowResize = () => {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(schedule, RESIZE_DEBOUNCE_MS);
  };
  window.addEventListener("resize", onWindowResize, { passive: true });

  return () => {
    ro.disconnect();
    window.removeEventListener("resize", onWindowResize);
    if (maskRafId != null) cancelAnimationFrame(maskRafId);
    clearTimeout(resizeTimeout);
    for (const { cutout } of bindings) {
      removeBannerKnockoutMaskDef(cutout);
    }
  };
}
